<template>
  <div v-if="mode === 'mini'">
    <h5 class="ui centered header">{{ selectedSearchName }}</h5>
    <div class="ui fluid vertical secondary menu" style="overflow: auto; max-height: 450px;">
      <template v-if="loading">
        <div class="ui placeholder">
          <div class="header">
            <div class="line"></div>
          </div>
          <div class="paragraph">
            <div class="line" v-for="i in perPage" :key="i"></div>
          </div>
        </div>
      </template>
      <template v-else-if="grouped.length > 1">
        <div class="item" v-for="g in grouped" :key="g.key">
          <div class="header">{{ g.name }}</div>
          <div class="menu">
            <router-link
              class="item"
              v-for="flow in sortPage(g.page)"
              :key="flow.id"
              :to="{
                name: 'app-flow',
                params: { appId: flow.application, flowId: flow.id, mode: 'code' }
              }"
            >
              {{ flow.name }}
            </router-link>
          </div>
        </div>
      </template>
      <template v-else>
        <router-link
          class="item"
          v-for="flow in sortPage(grouped[0].page)"
          :to="{
            name: 'app-flow',
            params: { appId: flow.application, flowId: flow.id, mode: 'code' }
          }"
          :key="flow.id"
        >
          {{ flow.name }}
        </router-link>
      </template>
    </div>
  </div>
  <div id="flows-wrapper" v-else>
    <vue-headful :title="$t('TITLE_TYPE', { type: $tc('FLOW', 2) }) | formatTitle"></vue-headful>
    <div class="ui basic segment" id="flows-controls">
      <h3 class="ui horizontal left aligned capitalize header">{{ $tc('FLOW', 2) }}</h3>

      <!-- GROUPING -->
      <div class="group control">
        <dropdown-select
          :labelText="$t('GROUP_BY', { thing: '...' })"
          :items="capitalizedGroupByOptions"
          :initialSelection="groupedBy"
          :emitInitialSelection="false"
          :multipleInitialSelections="true"
          @selected="selectGroupBy"
        />
      </div>

      <!-- SEARCH -->
      <div class="search control">
        <label>
          <b>{{ $t('FILTER_BY', { thing: '...' }) }}</b>
        </label>

        <tokenized-input
          :tokens="queryElements"
          :menus="queryMenus"
          @add="onTokenAdd"
          @menuShown="onQueryMenuShown"
          @menuHidden="onQueryMenuHidden"
        >
          <template #queryProp="{ model }">
            <div
              class="ui left labeled tiny query button"
              :class="{ active: menuShownForToken === model.id }"
            >
              <a class="ui tiny query label" :class="{ active: menuShownForToken === model.id }">
                <span class="prop">{{ model.prop.i18n }}</span>
                <span class="op">{{ $t(model.op.i18n) }}</span>
                <span class="value" v-if="model.value">{{ model.value }}</span>
              </a>
              <div
                class="ui icon tiny button"
                @click.stop="removeToken(model)"
                :class="{ active: menuShownForToken === model.id }"
              >
                <i class="ui close icon"></i>
              </div>
            </div>
          </template>

          <template #queryProp.menu="{ model, token }">
            <div class="ui vertical menu">
              <div class="item">
                <div class="header">{{ $t('FILTER_FLOWS_PROPERTY') }}</div>
                <div class="menu" v-if="searchableProperties && searchableProperties.length > 10">
                  <div class="item">
                    <div class="ui tiny icon input">
                      <input type="text" v-model="propertySearchTerm" />
                      <i class="ui search icon"></i>
                    </div>
                  </div>
                </div>
                <div class="scroller menu">
                  <a
                    v-for="p in model.properties"
                    :key="p.key"
                    class="item capitalize-fl"
                    @click="token.prop = p"
                    :class="{ active: p === token.prop }"
                  >
                    {{ p.i18n }}
                  </a>
                </div>
              </div>
              <div class="item">
                <div class="header">{{ $t('FILTER_FLOWS_OPERATOR') }}</div>
                <div class="scroller menu">
                  <a
                    v-for="op in model.operators"
                    :key="op.symbol"
                    class="item capitalize-fl"
                    @click="token.op = op"
                    :class="{ active: op.symbol === token.op.symbol }"
                  >
                    {{ $t(op.i18n) }}
                  </a>
                </div>
              </div>
              <div class="item" v-if="!token.op || !token.op.unary">
                <div class="header">{{ $t('FILTER_FLOWS_VALUE') }}</div>
                <div class="menu">
                  <div class="item">
                    <div class="ui icon input">
                      <input type="text" v-model="token.value" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </tokenized-input>
      </div>

      <!-- SAVED SEARCHES -->
      <div class="saved control">
        <saved-views
          :views="savedSearches"
          :selectedViewId="selectedSearch"
          @goto="gotoSearch"
          @delete="deleteSearch"
          @save="saveSearch"
        />
      </div>

      <!-- BUTTONS -->
      <div class="buttons control">
        <div class="ui small button" @click="saveSearch" v-if="!selectedSearch">
          {{ $t('NEW', { thing: $tc('SAVED_VIEW', 1) }) }}
        </div>
        <div
          class="ui small button"
          @click="saveSearch(currentSearch)"
          v-else-if="selectedSearchEdited"
        >
          {{ $t('EDIT', { thing: $tc('SAVED_VIEW', 1) }) }}
        </div>
      </div>
    </div>

    <div id="flows-tables">
      <div class="ui basic segment" v-for="g in grouped" :key="g.key">
        <h3 class="header">{{ g.name }}</h3>
        <kitchen-table
          :page="g.page"
          :columns="displayedColumns"
          :allowColumnSelection="true"
          i18nItemItemCountKey="FLOW_COUNT"
          :maxHeight="'100vh - 400px'"
          @filter-by="onFilterBy"
          @sort-by="onSortBy"
          @column-changed="onColumnChanged"
          @column-order-changed="onColumnOrderChanged"
        >
          <template #id.header="{ column }">
            <i>{{ column.key }}</i>
          </template>

          <!-- ID -->
          <template #id="{ cell }">
            <b>{{ cell }}</b>
          </template>

          <!-- NAME -->
          <template #name="{ row }">
            <router-link
              :to="{
                name: 'app-flow',
                params: { flowId: row.id, appId: row.application, mode: 'auto' }
              }"
            >
              {{ row.name }}
            </router-link>
          </template>

          <!-- APPLICATION -->
          <template #application="{ cell }">
            <router-link
              :to="{
                name: 'app-settings',
                params: { appId: cell, mode: 'auto' }
              }"
            >
              {{ nameOfApp(cell) }}
            </router-link>
          </template>

          <!-- SIZE -->
          <template #size="{ row, reduced }">
            <div style="display: inline-block; width: calc(100% - 75px);">
              <div
                style="display: inline-block; background-color: #ecedee; height: 5px; border-radius: 2px;"
                :style="{
                  width: `${(((row.code && row.code.length) || 0) / reduced.maxSize) * 100}%`
                }"
              ></div>
            </div>
            <div style="float: right;">{{ byteLength(row.code) }}</div>
          </template>

          <!-- MAINTAINER -->
          <template #maintainer="{ value }">
            <router-link
              :to="{
                name: 'user',
                params: { userId: value.id }
              }"
              v-if="value && value.id"
            >
              {{ value.name }}
            </router-link>
            <i v-else />
          </template>

          <!-- GROUPS -->
          <template #groups="{ value }">
            <div class="ui circular mini label">{{ value.groups.length }}</div>
            <multi-color-label
              style="float: right; margin: 5px;"
              size="tiny"
              :colors="value.colors"
            />
          </template>

          <!-- HIDE FROM USER -->
          <template #hidefromuser="{ cell }">
            <i class="ui green check icon" v-if="cell" style="float: right;" />
            <i v-else />
          </template>

          <!-- PRECOMPILE -->
          <template #precompile="{ cell }">
            <i class="ui green check icon" v-if="cell" style="float: right;" />
            <i v-else />
          </template>

          <!-- MANDATORY -->
          <template #ismandatory="{ cell }">
            <i class="ui green check icon" v-if="cell" style="float: right;" />
            <i v-else />
          </template>

          <!-- ANCHORS -->
          <template #anchors="{ value }">
            <ul>
              <li v-for="field in value" :key="field.id">
                <router-link
                  :to="{
                    name: 'app-field',
                    params: { fieldUd: field.id, appId: field.application }
                  }"
                >
                  {{ field.name }}
                </router-link>
              </li>
            </ul>
          </template>

          <!-- USES FLOWS -->
          <template #usesflows="{ value }">
            <ul>
              <li v-for="flow in value" :key="flow.id">
                <router-link
                  :to="{
                    name: 'app-flow',
                    params: { flowId: flow.id, appId: flow.application, mode: 'auto' }
                  }"
                >
                  {{ flow.name }}
                </router-link>
              </li>
            </ul>
          </template>

          <!-- USES FIELDS -->
          <template #usesfields="{ value }">
            <ul>
              <li v-for="field in value" :key="field.id">
                <router-link
                  :to="{
                    name: 'app-field',
                    params: { fieldId: field.id, appId: field.application }
                  }"
                >
                  {{ field.name }}
                </router-link>
              </li>
            </ul>
          </template>

          <!-- INPUTS -->
          <template #inputs="{ row }">
            <ul>
              <li v-for="input in row.inputs" :key="input.name">
                {{ input.name }} ({{ input.type }})
              </li>
            </ul>
          </template>

          <!-- OUTPUTS -->
          <template #outputs="{ row }">
            <ul>
              <li v-for="output in row.outputs" :key="output.name">
                {{ output.name }} ({{ output.type }})
              </li>
            </ul>
          </template>

          <!-- TYPE -->
          <template #type="{ row }">
            <i
              :class="
                `${
                  row.publication ? publicationTypeIcons[row.publication.type] : typeIcons[row.type]
                } icon`
              "
              :title="(row.publication && row.publication.type) || row.type"
            />
          </template>

          <!-- TYPECONFIG/ACTION -->
          <template #typeconfig.action="{ cell }">
            {{ cell }}
          </template>

          <!-- TYPECONFIG/SUBJECT -->
          <template #typeconfig.subject="{ cell }">
            {{ cell }}
          </template>

          <!-- TYPECONFIG/TRIGGER -->
          <template #typeconfig.triggers="{ row }">
            <ul>
              <template v-for="(triggers, type) in row.typeConfig && row.typeConfig.triggers">
                <li v-for="(trigger, idx) in triggers" :key="idx + type">
                  <TriggerCell :trigger="trigger" :type="type" :nameOfField="nameOfField" />
                </li>
              </template>
            </ul>
          </template>

          <template #publication.application="{ cell }">
            {{
              publicationApplications &&
                publicationApplications[cell] &&
                publicationApplications[cell].name
            }}
          </template>
          <template #publication.category="{ cell }">
            {{
              publicationCategories &&
                publicationCategories[cell] &&
                publicationCategories[cell].name
            }}
          </template>
        </kitchen-table>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../../scss/variables';

#flows-wrapper {
  padding-left: 10px;
  display: grid;
  grid-template-rows: 1fr 100fr;
  grid-template-areas:
    'header'
    'content';
}

#flows-controls {
  grid-area: header;
  display: grid;
  grid-template-columns: 3fr 1fr;
  grid-template-areas:
    'headline headline'
    'group saved'
    'search saved'
    'buttons saved';

  // The body-scrollable tables mess with z-index, so this is needed
  z-index: 9000;

  .text {
    .label {
      margin: 0 10px 0 10px;
    }
  }

  .control {
    padding: 1em;
  }

  .header {
    grid-area: headline;
  }

  .control.buttons {
    grid-area: buttons;
    float: left;
  }

  .control.saved {
    grid-area: saved;
  }

  .control.group {
    grid-area: group;
  }

  .control.search {
    grid-area: search;

    .query.button {
      .op {
        padding-right: 3px;
        padding-left: 3px;
        font-weight: normal;
      }

      .prop,
      .value {
        font-weight: bold;
      }

      margin: 2px;
    }
  }
}

#flows-tables {
  grid-area: content;
  overflow: auto;
}

.scroller {
  max-height: 150px;
  overflow-y: auto;
  overflow-x: hidden;
  text-overflow: ellipsis;
}

table ul {
  padding-inline-start: 20px;
}
</style>

<script>
// @flow
import Vue from 'vue'
import { Flow, Application, Group, User, Field, Hub } from 'services'
import { byteLength } from 'utility'
import {
  KitchenTable,
  KitchenPage,
  InMemoryPager,
  TokenizedInput,
  DropdownSelect,
  MultiColorLabel,
  SavedViews
} from 'ui'

import { TriggerCell } from './Triggers'

const operators = {
  '==': {
    symbol: '==',
    predicate: (candidate, target) => candidate == target,
    i18n: 'SEARCH_OPERATOR_EQUALS'
  },
  '!=': {
    symbol: '!=',
    predicate: (candidate, target) => candidate != target,
    i18n: 'SEARCH_OPERATOR_NOT_EQUALS'
  },
  '<': {
    symbol: '<',
    predicate: (candidate, target) => candidate < target,
    i18n: 'SEARCH_OPERATOR_LESS_THAN'
  },
  '<=': {
    symbol: '<=',
    predicate: (candidate, target) => candidate <= target,
    i18n: 'SEARCH_OPERATOR_LESS_THAN_OR_EQUALS'
  },
  '>': {
    symbol: '>',
    predicate: (candidate, target) => candidate > target,
    i18n: 'SEARCH_OPERATOR_GREATER_THAN'
  },
  '>=': {
    symbol: '>=',
    predicate: (candidate, target) => candidate >= target,
    i18n: 'SEARCH_OPERATOR_GREATER_THAN_OR_EQUALS'
  },
  '∋': {
    symbol: '∋',
    predicate: (candidate, target) => candidate && candidate.indexOf(target) >= 0,
    i18n: 'SEARCH_OPERATOR_CONTAINS'
  },
  '!∋': {
    symbol: '!∋',
    match: /^d\["(.+)"\]\.indexOf\("?(.+)"?\)===-1$/,
    i18n: 'SEARCH_OPERATOR_CONTAINS_NOT'
  },
  startswith: {
    symbol: 'startswith',
    predicate: (candidate, target) => candidate && candidate.indexOf(target) === 0,
    i18n: 'SEARCH_OPERATOR_STARTS_WITH'
  },
  endswith: {
    symbol: 'endswith',
    predicate: (candidate, target) =>
      candidate && candidate.indexOf(target, candidate.length - target.length) !== -1,
    i18n: 'SEARCH_OPERATOR_ENDS_WITH'
  },
  regexp: {
    symbol: 'regexp',
    predicate: (candidate, target) => target && candidate && new Regexp(target).test(candidate),
    i18n: 'SEARCH_OPERATOR_MATCHES_REGEXP'
  },
  true: {
    symbol: 'true',
    predicate: candidate => candidate,
    unary: true,
    i18n: 'SEARCH_OPERATOR_IS_TRUE'
  },
  false: {
    symbol: 'false  ',
    predicate: candidate => !candidate,
    unary: true,
    i18n: 'SEARCH_OPERATOR_IS_FALSE'
  }
}

const defaultGroupBy = ''
const defaultFilterWith = '*'
const defaultColumns = 'name,maintainer,size'
const defaultSortBy = '*'

export default {
  name: 'flows',

  components: {
    KitchenTable,
    TokenizedInput,
    DropdownSelect,
    MultiColorLabel,
    TriggerCell,
    SavedViews
  },

  props: {
    groupBy: { type: String, default: () => defaultGroupBy },
    filterWith: { type: String, default: () => defaultFilterWith },
    columns: { type: String, default: () => defaultColumns },
    sortBy: { type: String, default: () => defaultSortBy },
    selectedSearch: { type: String, default: () => null },
    mode: { type: String, default: () => 'default' },
    perPage: { type: Number, default: () => 50 }
  },

  data() {
    return {
      savedSearches: [],
      propertySearchTerm: '',
      menuShownForToken: null,
      queryElements: this.decodeFilter(this.filterWithC),
      loading: true,
      savedSearchesPromise: null,
      groupByD: this.groupBy,
      filterWithD: this.filterWith,
      columnsD: this.columns,
      sortByD: this.sortBy,
      selectedSearchEdited: false,
      searchableProperties: [
        { i18n: this.$tc('SUBJECT', 1), key: 'subjectPrefix' },
        { i18n: this.$tc('STATE_SUBJECT', 1), key: 'typeConfig.subject' },
        { i18n: this.$t('MANDATORY_STATE'), key: 'isMandatory' },
        { i18n: this.$t('HIDE_FROM_USER', { thing: this.$tc('FLOW', 1) }), key: 'hideFromUser' },
        { i18n: this.$t('CODE'), key: 'code' },
        { i18n: this.$t('NAME'), key: 'name' },
        { i18n: this.$t('FLOW_TYPE'), key: 'type' },
        {
          i18n: this.$t('COMPATIBLE_WITH'),
          key: 'compatibleVersion'
        },
        {
          i18n: this.$t('HAS', { thing: this.$t('BLOCKLY') }),
          key: 'hasBlockly',
          extractValue: f => f.blocklyXml && f.blocklyXml != ''
        },
        {
          i18n: this.$t('HAS', { thing: this.$tc('ANCHOR', 2) }),
          key: 'hasAnchors',
          extractValue: f => f.anchors && f.anchors.length > 0
        },
        {
          i18n: this.$t('HAS', { thing: this.$tc('INPUT', 2) }),
          key: 'hasInputs',
          extractValue: f => f.inputs && f.inputs.length > 0
        },
        {
          i18n: this.$t('HAS', { thing: this.$tc('OUTPUT', 2) }),
          key: 'hasOutputs',
          extractValue: f => f.outputs && f.outputs.length > 0
        },
        {
          i18n: this.$t('REFERENCES', { thing: this.$tc('FIELD', 2) }),
          key: 'usesFields',
          extractValue: f => f.usesFields && _.map(this.fieldsFor(f.usesFields), 'name')
        },
        {
          i18n: this.$t('REFERENCES', { thing: this.$tc('FLOW', 2) }),
          key: 'usesFlows',
          extractValue: f => f.usesFlows && _.map(this.flowsIn(f.usesFlows), 'name')
        },
        {
          i18n: this.$tc('ANCHOR', 2),
          key: 'anchors',
          extractValue: f => f.anchors && _.map(this.fieldsFor(f.anchors), 'name')
        },
        {
          i18n: this.$t('COUNT', { thing: this.$tc('TRIGGER', 2) }),
          key: 'triggerCount',
          extractValue: f =>
            f.typeConfig &&
            f.typeConfig.triggers &&
            _.size(f.typeConfig.triggers.external) +
              _.size(f.typeConfig.triggers.lifecycle) +
              _.size(f.typeConfig.triggers.context) +
              _.size(f.typeConfig.triggers.services)
        },
        {
          i18n: this.$t('HAS', { thing: this.$tc('TRIGGER', 2) }),
          key: 'hasTriggers',
          extractValue: f =>
            f.typeConfig &&
            f.typeConfig.triggers &&
            !(
              _.isEmpty(f.typeConfig.triggers.external) &&
              _.isEmpty(f.typeConfig.triggers.lifecycle) &&
              _.isEmpty(f.typeConfig.triggers.context) &&
              _.isEmpty(f.typeConfig.triggers.services)
            )
        },
        {
          i18n: this.$tc('MAINTAINER', 1),
          key: 'maintainerName',
          extractValue: f => {
            return this.userById(f.maintainer).name
          }
        },
        {
          i18n: this.$t('COUNT', { thing: this.$tc('GROUP', 2) }),
          key: 'groupCount',
          extractValue: f => this.groupsFor(f).length
        },
        {
          i18n: this.$t('SIZE'),
          key: 'sizeInBytes',
          convertInput: i => {
            // Allow specifying the amount in NN kB/mB etc style
            const [_, amount, unit] = /(\d+)\s*(\S*)/gi.exec(i)
            return amount * { b: 1, kb: 1000, mb: 1000000, gb: 1000000000 }[unit || 'b']
          },
          extractValue: f => f.code && f.code.length
        },
        {
          i18n: this.$tc('GROUP', 2),
          key: 'allGroups',
          extractValue: f => this.groupsFor(f)
        }
      ],
      groupByOptions: [
        {
          id: '*',
          name: this.$t('NO_SELECTION')
        },
        {
          id: 'app',
          name: this.$tc('APPLICATION', 1),
          findKeys: f => (f && f.application ? [f.application] : []),
          findName: key => this.nameOfApp(key)
        },
        {
          id: 'type',
          name: this.$t('FLOW_TYPE'),
          findKeys: f => [(f.publication && f.publication.type) || f.type]
        },
        {
          id: 'maintainer',
          name: this.$tc('MAINTAINER', 1),
          findKeys: f => [f.maintainer],
          findName: id => this.userById(id).name
        },
        {
          id: 'groups',
          name: this.$tc('GROUP', 2),
          findKeys: f => _.map(this.groupsFor(f), g => g.id),
          findName: key => this.nameOfGroup(key),
          costly: true
        },
        {
          id: 'trigger',
          name: this.$tc('TRIGGER', 2),
          findKeys: f =>
            _.map(
              _.concat(
                _.get(f, 'typeConfig.triggers.external'),
                _.get(f, 'typeConfig.triggers.context'),
                _.get(f, 'typeConfig.triggers.lifecycle'),
                _.get(f, 'typeConfig.triggers.services')
              ),
              t =>
                _.get(t, 'externalType') ||
                _.get(t, 'serviceType') ||
                _.get(t, 'event') ||
                this.$t('UNKNOWN')
            ),
          costly: true
        }
      ],

      cols: [
        {
          key: 'id',
          name: this.$t('ID'),
          sorts: true
        },
        {
          key: 'name',
          name: this.$t('NAME'),
          sorts: true
        },
        {
          key: 'application',
          name: this.$tc('APPLICATION', 1),
          value: f => _.get(_.find(this.apps, a => a.id == f.application), 'name'),
          sorts: true,
          hidden: true
        },
        {
          key: 'maintainer',
          name: this.$tc('MAINTAINER', 1),
          value: f => this.userById(f.maintainer),
          sorts: false,
          filters: true,
          hidden: false
        },
        {
          key: 'hideFromUser',
          name: this.$t('HIDE_FROM_USER', { thing: this.$tc('FLOW', 1) }),
          hidden: true
        },
        {
          key: 'precompile',
          name: this.$t('PRECOMPILE_FLOW'),
          hidden: true
        },
        {
          key: 'isMandatory',
          name: this.$t('MANDATORY_STATE'),
          hidden: true
        },
        {
          key: 'size',
          name: this.$t('SIZE'),
          sorts: true,
          // The reducer is run once on all input elements to produce an additional input to value etc
          reducer: (memo, f) => {
            return {
              maxSize:
                (memo.maxSize || 0) < ((f.code && f.code.length) || 0)
                  ? (f.code && f.code.length) || 0
                  : memo.maxSize || 0
            }
          },
          sortValue: f => (f.code ? f.code.length : 0)
        },
        {
          key: 'compatibleVersion',
          name: this.$t('COMPATIBLE_WITH'),
          sorts: true
        },
        {
          key: 'version',
          name: this.$tc('VERSION', 1)
        },
        {
          key: 'groups',
          name: this.$tc('GROUP', 2),
          sorts: true,
          value: f => {
            const groups = this.groupsFor(f)
            const colors = _.filter(
              _.map(groups, g => g.color),
              color => color !== Group.defaultColor
            )
            return { groups, colors }
          },
          sortValue: f => this.groupsFor(f).length
        },
        {
          key: 'anchors',
          name: this.$tc('ANCHOR', 2),
          sorts: false,
          value: f => this.fieldsFor(f.anchors)
        },
        {
          key: 'usesFlows',
          name: this.$t('REFERENCES', { thing: this.$tc('FLOW', 2) }),
          sorts: false,
          value: f => this.flowsIn(f.usesFlows)
        },
        {
          key: 'usesfields',
          name: this.$t('REFERENCES', { thing: this.$tc('FIELD', 2) }),
          sorts: false,
          value: f => this.fieldsFor(f.usesfields)
        },
        {
          key: 'inputs',
          name: this.$tc('INPUT', 2),
          sorts: false
        },
        {
          key: 'outputs',
          name: this.$tc('OUTPUT', 2),
          sorts: false
        },
        {
          key: 'type',
          name: this.$t('FLOW_TYPE')
        },
        {
          key: 'typeConfig.action',
          name: this.$tc('ACTION', 1)
        },
        {
          key: 'typeConfig.subject',
          name: this.$tc('SUBJECT', 1)
        },
        {
          key: 'typeConfig.triggers',
          name: this.$tc('TRIGGER', 2)
        },
        {
          key: 'publication.releaseNote.version',
          name: this.$t('PUBLICATION_VERSION')
        },
        {
          key: 'publication.abstract',
          name: this.$t('ABSTRACT')
        },
        {
          key: 'publication.application',
          name: this.$t('PUBLICATION_APP')
        },
        {
          key: 'publication.category',
          name: this.$tc('CATEGORY', 1)
        }
      ]
    }
  },

  computed: {
    capitalizedGroupByOptions() {
      return this.groupByOptions.map(o => ({ ...o, name: this.capitalize(o.name) }))
    },
    currentSearch() {
      return _.find(this.savedSearches, s => s.id === this.selectedSearch)
    },
    selectedSearchName() {
      return this.currentSearch?.name
    },
    groupByC() {
      return (this.groupBy !== defaultGroupBy ? this.groupBy : this.groupByD) || defaultGroupBy
    },
    filterWithC() {
      return (
        (this.filterWith !== defaultFilterWith ? this.filterWith : this.filterWithD) ||
        defaultFilterWith
      )
    },
    columnsC() {
      return (this.columns !== defaultColumns ? this.columns : this.columnsD) || defaultColumns
    },
    sortByC() {
      return (this.sortBy !== defaultSortBy ? this.sortBy : this.sortByD) || defaultSortBy
    },
    publicationCategories() {
      return _.keyBy(this.$store.state.hub.categories, 'identifier')
    },
    publicationApplications() {
      return _.keyBy(this.$store.state.hub.applications, 'identifier')
    },
    typeIcons() {
      return Flow.flowTypeIcons
    },
    publicationTypeIcons() {
      return _.reduce(
        Hub.publicationTypes(),
        (icons, pt) => {
          icons[pt.id] = `${pt.color} ${pt.icon}`
          return icons
        },
        {}
      )
    },
    displayedColumns() {
      const decodedColumns = _.split(this.columnsC, ',')
      return _.orderBy(
        _.map(this.cols, col => ({
          ...col,
          order: decodedColumns.indexOf(col.key),
          hidden: decodedColumns.indexOf(col.key) === -1
        })),
        'order'
      )
    },
    filteredFlows() {
      const query = this.decodeFilter(this.filterWithC)
      return _.filter(this.flows, flow => {
        return _.every(query, q => {
          // If there is a func to extract the value, we use it
          // otherwise the key is used to find the property
          const v = q.prop.extractValue ? q.prop.extractValue(flow) : _.get(flow, q.prop.key)
          const i = q.prop.convertInput ? q.prop.convertInput(q.value) : q.value
          return q.op.predicate ? q.op.predicate(v, i) : false
        })
      })
    },
    queryMenus() {
      return [
        {
          template: 'queryProp',
          properties: _.filter(
            this.searchableProperties,
            p =>
              (p.name && p.name.indexOf(this.propertySearchTerm) !== -1) ||
              (p.key && p.key.indexOf(this.propertySearchTerm) !== -1)
          ),
          operators: _.values(operators)
        }
      ]
    },
    flows() {
      return this.$store.state.flow.items
    },
    groupedBy() {
      return _.find(this.groupByOptions, o => o.id == this.groupByC)
    },
    fields() {
      return this.$store.state.field.items
    },
    apps() {
      return this.$store.state.application.items
    },
    groups() {
      return this.$store.state.group.items
    },
    users() {
      return this.$store.state.user.items
    },
    page() {
      return new KitchenPage(
        new InMemoryPager({
          items: this.filteredFlows,
          pageSize: this.perPage,
          loading: this.loading,
          sortBy: this.sortByC
        })
      )
    },
    grouped() {
      // split flow.items into groups
      if (this.groupedBy && this.groupedBy.id != '*') {
        // A flow can be part of multiple groups, therefore the reduce and not a simple _.groupBy
        const groupedFlows = _.reduce(
          this.filteredFlows,
          (result, flow) => {
            _.forEach(this.groupedBy.findKeys(flow), key => {
              if (result[key]) result[key].push(flow)
              else result[key] = [flow]
            })
            return result
          },
          {}
        )

        return _.map(groupedFlows, (flows, key) => {
          return {
            key: key,
            name: (this.groupedBy.findName && this.groupedBy.findName(key)) || key,
            page: new KitchenPage(
              new InMemoryPager({
                items: flows,
                pageSize: this.perPage,
                loading: this.loading,
                sortBy: this.sortByC
              })
            )
          }
        })
      }

      // return single group
      return [
        {
          key: '',
          name: '',
          page: new KitchenPage(
            new InMemoryPager({
              items: this.filteredFlows,
              pageSize: this.perPage,
              loading: this.loading,
              sortBy: this.sortByC
            })
          )
        }
      ]
    }
  },

  methods: {
    byteLength,
    sortPage(page) {
      const asc = page.sortBy.indexOf('!') < 0
      const sortedBy = _.sortBy(page.items, _.trimStart(page.sortBy, '!'))
      if (asc) _.reverse(sortedBy)
      return sortedBy
    },
    capitalize(s) {
      return this.$options.filters.capitalize(s)
    },
    edit(e) {
      if (this.selectedSearch) {
        // we modify the data-based properties
        this.groupByD = e.groupBy
        this.sortByD = e.sortBy
        this.filterWithD = e.filterWith
        this.columnsD = e.columns
        this.selectedSearchEdited = true
      } else {
        // we push a custom route

        this.$router.push({
          name: 'flows-custom',
          params: e
        })
      }
    },
    onFilterBy(evt) {
      if (_.find(this.queryElements, qe => qe.prop.key == evt.column.key && qe.op.key == '=='))
        return

      const query = this.queryElements.concat([
        {
          prop: { key: evt.column.key, i18n: this.$t('EXACT_MATCH', { thing: evt.column.key }) },
          op: operators['=='],
          value: evt.row[evt.column.key]
        }
      ])

      this.edit({
        groupBy: this.groupByC || '*',
        filterWith: this.encodeFilter(query),
        columns: this.columnsC,
        sortBy: this.sortByC
      })
    },
    onSortBy(sort) {
      const sortBy = `${sort.ascending ? '' : '!'}${sort.by}`
      if (this.sortByC == sortBy) return

      this.edit({
        groupBy: this.groupByC || '*',
        filterWith: this.filterWithC || '*',
        columns: this.columnsC,
        sortBy
      })
    },
    onColumnChanged(col) {
      // Add or remove the column changed?
      const columns = _.join(
        _.orderBy(
          _.filter(_.uniq(_.split(this.columnsC, ',').concat([col.key])), c => {
            return c != col.key || !col.hidden
          }),
          key => {
            const colDef = _.find(this.cols, col => col.key == key)
            return (colDef && colDef.order) || 0
          },
          ','
        )
      )
      this.edit({
        groupBy: this.groupByC || '*',
        filterWith: this.filterWithC || '*',
        columns,
        sortBy: this.sortByC
      })
    },
    onColumnOrderChanged(orderedColumns) {
      this.edit({
        groupBy: this.groupByC || '*',
        filterWith: this.filterWithC || '*',
        columns: _.join(
          _.map(
            _.orderBy(_.filter(orderedColumns, c => !c.hidden && c.order >= 0), 'order'),
            'key'
          ),
          ','
        )
      })
    },
    selectGroupBy(groupBy) {
      this.edit({
        groupBy: groupBy.id,
        filterWith: this.filterWithC,
        columns: this.columnsC,
        sortBy: this.sortByC
      })
    },
    onQueryMenuShown(m) {
      this.menuShownForToken = m.token.id
    },
    encodeFilter(elements) {
      return (
        _.join(_.map(elements, qe => `${qe.prop.key} ${qe.op.symbol} ${qe.value}`), ' && ') || '*'
      )
    },
    decodeFilter(f) {
      return _.filter(
        _.map(_.split(f, ' && '), qe => {
          const [propKey, opKey, ...values] = _.split(qe, ' ')
          return {
            prop: _.find(this.searchableProperties, p => p.key == propKey) || {
              key: propKey,
              i18n: this.$t('EXACT_MATCH', { thing: propKey })
            },
            op: operators[opKey],
            value: _.join(values, ' '),
            id: Math.random() * 1000,
            template: 'queryProp'
          }
        }),
        elm => elm.prop && elm.op
      )
    },
    onQueryMenuHidden() {
      this.menuShownForToken = null

      const filterWith = this.encodeFilter(this.queryElements)
      this.edit({
        groupBy: this.groupByC || '*',
        filterWith,
        columns: this.columnsC,
        sortBy: this.sortByC
      })
    },
    removeToken(token) {
      this.queryElements = _.filter(this.queryElements, q => q.id !== token.id)
      this.onQueryMenuHidden()
    },
    onTokenAdd() {
      this.queryElements.push({
        template: 'queryProp',
        id: Math.random() * 1000,
        prop: { key: '', i18n: '' },
        op: operators['=='],
        value: ''
      })
    },

    userById(userId) {
      return _.find(this.users, u => u.id == userId) || {}
    },

    flowsIn(flowIds) {
      if (!flowIds || flowIds.length == 0) return []

      return _.filter(this.flows, f => flowIds.indexOf(f.id) !== -1)
    },

    nameOfField(fieldId) {
      const fields = this.fieldsFor([fieldId])
      if (fields && fields.length > 0) return fields[0].name
      return fieldId
    },

    fieldsFor(fieldIds) {
      if (!fieldIds || fieldIds.length == 0) return []

      return _.filter(this.fields, f => fieldIds.indexOf(f.id) !== -1)
    },

    groupsFor(flow) {
      return _.filter(this.groups, g => _.find(g.flow, flowId => flowId == flow.id)) || []
    },

    nameOfGroup(id) {
      return _.get(_.find(this.groups, g => g.id == id), 'name')
    },

    nameOfApp(id) {
      return _.get(_.find(this.apps, a => a.id == id), 'name')
    },

    refreshSavedSearches() {
      this.savedSearchesPromise = Flow.getSearches().then(
        searches => (this.savedSearches = searches)
      )
    },

    deleteSearch(s) {
      Flow.deleteSearch(s.id).then(this.refreshSavedSearches)
    },

    gotoSearch(s) {
      if (!s) return

      this.$router.push({
        name: 'flows-saved',
        params: {
          selectedSearch: s.id
        }
      })
    },

    saveSearch(s) {
      // take the values from the ui, name from s
      Flow.saveSearch(this.selectedSearch, s?.version, {
        groupBy: this.groupByC,
        filterWith: this.encodeFilter(this.queryElements),
        columns: this.columnsC,
        sortBy: this.sortByC,
        name: s?.name || this.$i18n.t('NEW', { thing: this.$i18n.tc('SAVED_VIEW', 1) })
      })
        .then(this.refreshSavedSearches)
        .then(() => (this.selectedSearchEdited = false))
    },

    initializeFromSavedSearch() {
      if (!this.selectedSearch) return
      ;(this.savedSearchesPromise || Flow.getSearches()).then(searches => {
        const search = _.find(searches, s => s.id === this.selectedSearch)
        this.columnsD = search.columns
        this.groupByD = search.groupBy
        this.sortByD = search.sortBy
        this.filterWithD = search.filterWith
        this.queryElements = this.decodeFilter(this.filterWithC)
      })
    }
  },

  mounted() {
    Flow.readAllFlows().then(() => (this.loading = false))
    Application.readAllApps()
    Field.readFields()
    Group.readAllGroups()
    User.readAllUsers()

    Hub.list()

    this.refreshSavedSearches()
    this.initializeFromSavedSearch()
    this.queryElements = this.decodeFilter(this.filterWithC)
  },

  watch: {
    filterWith: function(newFilter) {
      this.queryElements = this.decodeFilter(newFilter)
    },
    selectedSearch: function(newSavedSearch) {
      if (!newSavedSearch) return
      this.initializeFromSavedSearch()
    }
  }
}
</script>
